// Cheap checks for transcript invariants the screen can prove broken; counts only.
import type { StreamEntry } from "./state.svelte.js";
import type { PromptVerdict } from "./turn-prompts.js";

type Prompt = StreamEntry & { kind: "user_message" };

const isPrompt = (e: StreamEntry): e is Prompt => e.kind === "user_message";

function seqsOfPrompt(e: Prompt): number[] {
  const out = e.rows ? [...e.rows] : [];
  if (e.seq !== undefined) out.push(e.seq);
  return out;
}

// Bubbles provably one send twice: a shared client id, seq or message id, or a
// pending bubble whose settled bubble (the ledger) is also on screen.
export function duplicatePromptBubbles(
  entries: readonly StreamEntry[],
  settledEntryIdByClientId: ReadonlyMap<string, string> = new Map(),
): number {
  const prompts = entries.filter(isPrompt);
  const onScreen = new Set(prompts.map((p) => p.id));
  const seen = { client: new Set<string>(), seq: new Set<number>(), message: new Set<string>() };
  let duplicates = 0;
  for (const p of prompts) {
    let dup = false;
    if (p.clientMessageId) {
      if (seen.client.has(p.clientMessageId)) dup = true;
      seen.client.add(p.clientMessageId);
      const settled = settledEntryIdByClientId.get(p.clientMessageId);
      if (settled && settled !== p.id && onScreen.has(settled)) dup = true;
    }
    for (const seq of new Set(seqsOfPrompt(p))) {
      if (seen.seq.has(seq)) dup = true;
      seen.seq.add(seq);
    }
    if (p.messageId) {
      if (seen.message.has(p.messageId)) dup = true;
      seen.message.add(p.messageId);
    }
    if (dup) duplicates += 1;
  }
  return duplicates;
}

const settledUnsigned = (p: Prompt): boolean =>
  !p.promptAuthor && !p.clientMessageId && p.status === undefined && !p.rehydrated;

// A settled unsigned bubble with a server-stamped twin of the same words in its turn.
// Unsigned alone is legitimate (a scheduled send); the stamped twin proves a lost fold.
export function unattributedTwinBubbles(entries: readonly StreamEntry[]): number {
  let count = 0;
  let turn: Prompt[] = [];
  const flush = () => {
    const signed = new Set(turn.filter((p) => !!p.promptAuthor).map((p) => p.content.trim()));
    for (const p of turn) if (settledUnsigned(p) && signed.has(p.content.trim())) count += 1;
    turn = [];
  };
  for (const e of entries) {
    if (e.kind === "turn_boundary") flush();
    else if (isPrompt(e)) turn.push(e);
  }
  flush();
  return count;
}

export const OUT_OF_ORDER_MS = 60 * 60 * 1000;

// Of `ids`, how many sit below a row more than an hour newer.
export function outOfOrderRows(
  entries: readonly StreamEntry[],
  ids: ReadonlySet<string>,
  thresholdMs: number = OUT_OF_ORDER_MS,
): number {
  let newest = Number.NEGATIVE_INFINITY;
  let count = 0;
  for (const e of entries) {
    if (e.ts === undefined) continue;
    if (ids.has(e.id) && newest - e.ts > thresholdMs) count += 1;
    newest = Math.max(newest, e.ts);
  }
  return count;
}

// Durable prompts read without a row seq below the newest of `ids` (entries a live committed
// row just added): a seq'd row is seated only among seq'd rows, so it lands above them. A
// local send (clientMessageId) is not counted — a late row of the turn before sits above it.
// `verdict` (the row's turn): a prompt that came after its turn started is newer, not misplaced.
export function promptsBelowLiveRow(
  entries: readonly StreamEntry[],
  ids: ReadonlySet<string>,
  verdict?: PromptVerdict,
): number {
  const last = entries.findLastIndex((e) => ids.has(e.id));
  if (last < 0) return 0;
  let count = 0;
  for (const e of entries.slice(last + 1)) {
    if (!isPrompt(e) || e.clientMessageId || seqsOfPrompt(e).length > 0) continue;
    if (verdict?.(e.id) === false) break; // every prompt below one proved newer is newer too
    count += 1;
  }
  return count;
}

export function newTextSince(
  entries: readonly StreamEntry[],
  before: ReadonlySet<string>,
  sinceTs: number,
): number {
  return entries.filter(
    (e) =>
      e.kind === "text" &&
      !before.has(e.id) &&
      e.content.trim() !== "" &&
      e.ts !== undefined &&
      e.ts >= sinceTs,
  ).length;
}

// Live text frames per turn watched from its start; a hidden/offline turn is not judged.
export class TurnDeltaTracker {
  private readonly turns = new Map<
    string,
    { deltas: number; tainted: boolean; startedAt: number }
  >();

  observe(
    agentId: string,
    event: { type: string; item?: { type?: string } },
    now: number = Date.now(),
    hidden = false,
  ): void {
    if (event.type === "turn_started") {
      this.turns.set(agentId, { deltas: 0, tainted: hidden, startedAt: now });
      return;
    }
    const turn = this.turns.get(agentId);
    if (turn && event.type === "timeline" && event.item?.type === "assistant_message") {
      turn.deltas += 1;
    }
  }

  taintAll(): void {
    for (const turn of this.turns.values()) turn.tainted = true;
  }

  finishSilent(agentId: string): number | undefined {
    const turn = this.turns.get(agentId);
    this.turns.delete(agentId);
    return turn && !turn.tainted && turn.deltas === 0 ? turn.startedAt : undefined;
  }

  reset(): void {
    this.turns.clear();
  }

  size(): number {
    return this.turns.size;
  }
}
